/* eslint-disable no-magic-numbers */

import { FinancialConfig } from '@futrli-sdk/predict';
import { range } from 'lodash';

import { AssumptionData } from '../../../../../core/types';
import { FreestyleMethodState, FreestyleMethodYear } from '../../../types';
import { getDatesBetweenTodayAndNYears, forceLocalDate } from '../../../../../utils/dates';
import { getDateFY, getDateFYStartEnd, getFYStartEnd } from '../../../../../utils/financial-year-end';

/**
 * Returns true or false depending upon if the given freestyle content is considered to be empty
 */
export const checkIfFreestyleContentIsEmpty = (content: FreestyleMethodState): boolean => {
  const years = Object.keys(content);
  for (const year of years) {
    const yearInfo = content[year];
    for (const data of yearInfo) {
      if (data.value) {
        return false;
      }
    }
  }
  return true;
};

/**
 * Generates data for financial year rows for the given financial year range.
 * Be aware that 2022 would represent FY2022, which means 2021-2022 for an org whos FY spans 2 years.
 * @param financialConfig - Financial config for the organisation
 * @param from - The financial year to start generating rows from (YYYY)
 * @param to - The final financial year to generate a row for (YYYY)
 */
export const generateFinancialYearsData = (financialConfig: FinancialConfig, from: number, to: number) => {
  const data: Record<string, FreestyleMethodYear[]> = {};

  range(from, to + 1).forEach(year => {
    const fyStart = getFYStartEnd(financialConfig, year).start;
    Object.assign(data, generateYearData(fyStart));
  });

  return data;
};

/**
 * Generates data for a year row from the given date
 * @param from - The date to generate the row from (YYYY-MM-DD)
 */
export const generateYearData = (from: string): Record<string, FreestyleMethodYear[]> => {
  const fromDate = forceLocalDate(from);
  const isCalendarYear = fromDate.getDate() === 1 && fromDate.getMonth() + 1 === 1;
  const monthDates = getDatesBetweenTodayAndNYears(1, fromDate).slice(0, -1);
  const financialYear = fromDate.getFullYear() + (isCalendarYear ? 0 : 1);
  const values: FreestyleMethodYear[] = monthDates.map(d => ({
    date: d.substring(0, d.length - 3), // "2022-03-01" -> "2022-03" (removing day from string)
    value: '',
    selected: false
  }));

  return {
    [financialYear]: values
  };
};

/**
 * Will return the earliest and latest financial years that the given amounts span (returned as an array of two numbers)
 * Will return array of undefined if the amount array is empty
 * @param financialConfig - Financial config for the organisation
 * @param amounts - The amounts array
 */
export const getEarliestAndLatestFinancialYears = (financialConfig: FinancialConfig, amounts: AssumptionData[]) => {
  if (!amounts.length) {
    return [undefined, undefined];
  }

  const sortedDates = amounts
    .map(amount => amount.date)
    .map(dateStr => forceLocalDate(dateStr))
    .sort((a, b) => a.getTime() - b.getTime());

  const earliestAndLatestDate = [sortedDates[0], sortedDates[sortedDates.length - 1]];

  return earliestAndLatestDate.map(date => {
    const fyDates = getFYStartEnd(financialConfig, date.getFullYear());
    const financialYearEnd = forceLocalDate(fyDates.end);
    return date < financialYearEnd ? date.getFullYear() : date.getFullYear() + 1;
  });
};

/**
 * Formatting existing amounts to backend structure
 * @param freestyleContent current format of freestyle content amounts
 */
export const getFormattedAmounts = (freestyleContent: FreestyleMethodState): AssumptionData[] => {
  const formattedAmounts: AssumptionData[] = [];
  Object.keys(freestyleContent)
    .sort((a, b) => Number(a) - Number(b))
    .forEach(yearKey => {
      const a = freestyleContent[yearKey].filter(d => d.value.length > 0).map(d => ({ date: d.date, amount: d.value }));
      formattedAmounts.push(...a);
    });

  return formattedAmounts;
};

/**
 * Get initial freestyle content for adding new freestyle manual prediction
 */
export const getFreestyleInitialContent = (
  financialConfig: FinancialConfig,
  forecastFromDate?: string | null,
  forecastKind?: 'live' | 'budget' | 'scenario'
) => {
  if (forecastKind === 'budget') {
    // For budget we need 12 months from forecast from date
    // If no forecast from date, will generate 12 months from today - bug?
    return generateYearData(forecastFromDate || '');
  }

  const forecastFYDates = getDateFYStartEnd(financialConfig, forecastFromDate);
  const currentFY = getDateFY(financialConfig, financialConfig.current_year.start);
  const forecastFY = getDateFY(financialConfig, forecastFYDates.start);

  const startYear = forecastFY;
  const endYear = Math.max(forecastFY, currentFY);

  return {
    // current financial year
    ...generateFinancialYearsData(financialConfig, startYear, endYear),
    // second financial year
    ...generateFinancialYearsData(financialConfig, startYear + 1, endYear + 1)
  };
};

/**
 * Get initial freestyle content for updating existing freestyle manual prediction
 */
export const getUpdateFreestyleInitialContent = (
  financialConfig: FinancialConfig,
  amounts: AssumptionData[],
  showDecimals: boolean,
  forecastFromDate?: string | null,
  forecastKind?: 'live' | 'scenario' | 'budget'
) => {
  let initialData: Record<string, FreestyleMethodYear[]> = {};

  if (forecastKind === 'budget') {
    initialData = generateYearData(forecastFromDate || '');
  } else {
    const forecastFYDates = getDateFYStartEnd(financialConfig, forecastFromDate);
    const currentFY = getDateFY(financialConfig, financialConfig.current_year.start);
    const forecastFY = getDateFY(financialConfig, forecastFYDates.start);
    const [minDataYear = currentFY, maxDataYear = currentFY] = getEarliestAndLatestFinancialYears(
      financialConfig,
      amounts
    );

    const startYear = Math.min(minDataYear, forecastFY);
    const endYear = Math.max(maxDataYear, forecastFY, currentFY);

    initialData = generateFinancialYearsData(financialConfig, startYear, endYear);
  }

  return updateValues(initialData, amounts, showDecimals);
};

/**
 * Will update the target object with the given values. Any cells without a value specified will be left as-is.
 * @param target - target object to update
 * @param amounts - the amounts to update the target with
 * @param showDecimals - wether or not the values should be shown as decimals
 */
export const updateValues = (
  target: Record<string, FreestyleMethodYear[]>,
  amounts: AssumptionData[],
  showDecimals: boolean
) => {
  const amountsObj: Record<string, string> = {};

  // Build object of date/amount so its easy to reference in a moment
  amounts.forEach(amount => {
    amountsObj[amount.date.substring(0, amount.date.length - 3)] = amount.amount;
  });

  // Now update the target object values
  Object.values(target).forEach(yearRow => {
    yearRow.forEach(cell => {
      if (amountsObj[cell.date]) {
        cell.value = showDecimals ? amountsObj[cell.date] : String(Math.round(Number(amountsObj[cell.date])));
      }
    });
  });

  return target;
};
